// React imports
import { useMemo } from 'react'

// External SDK imports
import { useChain } from '@azuro-org/sdk'
import { useChainId } from 'wagmi'

// Type imports
import type { Coins } from 'thales-utils'
import type { SupportedNetwork } from '@/Overtime/types/network'

// Internal utilities
import { isOvertimeSupportedChain } from '@/utils/helper'
import {
  getCollateralAddress,
  getCollaterals,
} from '@/Overtime/utils/collaterals'
import { useTokenSelection } from '@/contexts/TokenSelectionContext'

// Import the AppChainProvider enum and getAppChainProvider function
enum AppChainProvider {
  Overtime = 'overtime',
  SxBet = 'sx',
  Azuro = 'azuro',
}

// Re-export the getAppChainProvider function since it's not exported from helper.ts
const getAppChainProvider = (chainId: number): AppChainProvider | null => {
  // Input validation
  if (
    typeof chainId !== 'number' ||
    !Number.isInteger(chainId) ||
    chainId <= 0
  ) {
    return null
  }

  // Import constants dynamically to avoid circular dependencies
  const constants = require('@/helpers/constants').default

  // Check Overtime chains first (if you have priority preference)
  if (constants.OVERTIME_CHAIN_IDS.has(chainId)) {
    return AppChainProvider.Overtime
  }

  // Check SX chains
  // if (constants.SX_BET_CHAIN_IDS.has(chainId)) {
  //   return AppChainProvider.SxBet
  // }

  if (constants.AZURO_CHAIN_IDS.has(chainId)) {
    return AppChainProvider.Azuro
  }

  return null
}

/**
 * Token information for a specific chain
 */
export interface TokenInfo {
  /** Token symbol (e.g., 'USDC', 'WETH', 'POL') */
  symbol: string
  /** Token contract address */
  address: string
  /** Token decimals */
  decimals: number
  /** Whether this is the default token for the chain */
  isDefault: boolean
}

/**
 * Chain provider information with available tokens
 */
export interface ChainProviderInfo {
  /** The chain ID */
  chainId: number
  /** The provider type (overtime, azuro, etc.) */
  provider: 'overtime' | 'azuro' | 'sx' | null
  /** Whether this is an Overtime-supported chain */
  isOvertimeChain: boolean
  /** Whether this is an Azuro-supported chain */
  isAzuroChain: boolean
  /** Whether this is an SX-supported chain */
  isSXChain: boolean
  /** Available tokens for this chain */
  tokens: TokenInfo[]
  /** Default token for this chain */
  defaultToken: TokenInfo | null
  /** Get token by symbol */
  getTokenBySymbol: (symbol: string) => TokenInfo | null
  /** Get token by address */
  getTokenByAddress: (address: string) => TokenInfo | null
  /** Get token by index (for Overtime chains) */
  getTokenByIndex: (index: number) => TokenInfo | null
}

/**
 * Hook that provides current chain provider information along with available token addresses
 *
 * This hook determines the current chain's provider (Overtime, Azuro, etc.) and returns
 * all available token addresses for that chain. For Overtime chains, it provides multiple
 * collateral options, while for Azuro chains, it provides the single bet token.
 *
 * For Base chain, the provider is determined by the selected token:
 * - WETH → Azuro provider
 * - USDC → Overtime provider
 *
 * @returns ChainProviderInfo object with provider details and token information
 *
 * @example
 * ```typescript
 * // Get provider info with current token selection
 * const { provider, tokens, defaultToken } = useChainProvider()
 *
 * // Get all available tokens
 * console.log(tokens) // [{ symbol: 'USDC', address: '0x...', decimals: 6, isDefault: true }, ...]
 *
 * // Get specific token
 * const usdcToken = getTokenBySymbol('USDC')
 *
 * // Check if it's an Overtime chain
 * if (isOvertimeChain) {
 *   // Multiple tokens available
 *   const wethToken = getTokenByIndex(6) // WETH token
 * }
 * ```
 */
export const useChainProvider = (): ChainProviderInfo => {
  const chainId = useChainId()
  const { betToken } = useChain()
  const { selectedTokenIndex } = useTokenSelection()

  return useMemo(() => {
    if (!chainId) {
      return {
        chainId: 0,
        provider: null,
        isOvertimeChain: false,
        isAzuroChain: false,
        isSXChain: false,
        tokens: [],
        defaultToken: null,
        getTokenBySymbol: () => null,
        getTokenByAddress: () => null,
        getTokenByIndex: () => null,
      }
    }

    // Get base provider first
    const baseProvider = getAppChainProvider(chainId)
    const isOvertimeSupported = isOvertimeSupportedChain(chainId)

    // Special handling for Base chain (chainId: 8453)
    let provider: 'overtime' | 'azuro' | 'sx' | null = baseProvider
    let isOvertimeChain = isOvertimeSupported
    let isAzuroChain = baseProvider === 'azuro'
    let isSXChain = baseProvider === 'sx'

    // For Base chain, determine provider based on selected token
    if (chainId === 8453 && selectedTokenIndex !== undefined) {
      const networkId = chainId as SupportedNetwork
      const availableCollaterals = getCollaterals(networkId) || []
      const selectedCollateral = availableCollaterals[selectedTokenIndex]

      if (selectedCollateral) {
        // For Base chain: USDC (index 0) → Overtime, WETH (index 5) → Azuro
        if (selectedCollateral === 'WETH') {
          provider = 'azuro'
          isOvertimeChain = false
          isAzuroChain = true
          isSXChain = false
        } else if (selectedCollateral === 'USDC') {
          provider = 'overtime'
          isOvertimeChain = true
          isAzuroChain = false
          isSXChain = false
        }
        // For other tokens on Base chain, default to Overtime
        else {
          provider = 'overtime'
          isOvertimeChain = true
          isAzuroChain = false
          isSXChain = false
        }
      }
    }

    // Get tokens based on provider
    let tokens: TokenInfo[] = []
    let defaultToken: TokenInfo | null = null

    if (isOvertimeChain) {
      // For Overtime chains, get all available collaterals
      const networkId = chainId as SupportedNetwork
      const availableCollaterals = getCollaterals(networkId) || []

      tokens = availableCollaterals.map((collateral: Coins, index: number) => {
        const address = getCollateralAddress(networkId, index)
        const isDefault = index === 0 // First token is default

        return {
          symbol: collateral,
          address: address || '',
          decimals: getCollateralDecimals(collateral),
          isDefault,
        }
      })

      // Set default token (first in the list)
      defaultToken = tokens[0] || null
    } else if (isAzuroChain) {
      // For Azuro chains, use the bet token from the SDK
      const azuroToken: TokenInfo = {
        symbol: betToken.symbol,
        address: betToken.address,
        decimals: betToken.decimals,
        isDefault: true,
      }

      tokens = [azuroToken]
      defaultToken = azuroToken
    }

    // Helper functions
    const getTokenBySymbol = (symbol: string): TokenInfo | null => {
      return (
        tokens.find(
          token => token.symbol.toUpperCase() === symbol.toUpperCase()
        ) || null
      )
    }

    const getTokenByAddress = (address: string): TokenInfo | null => {
      return (
        tokens.find(
          token => token.address.toLowerCase() === address.toLowerCase()
        ) || null
      )
    }

    const getTokenByIndex = (index: number): TokenInfo | null => {
      return tokens[index] || null
    }

    return {
      chainId,
      provider,
      isOvertimeChain,
      isAzuroChain,
      isSXChain,
      tokens,
      defaultToken,
      getTokenBySymbol,
      getTokenByAddress,
      getTokenByIndex,
    }
  }, [chainId, betToken, selectedTokenIndex])
}

/**
 * Get decimals for a collateral token
 * @param collateral - The collateral symbol
 * @returns The number of decimals
 */
const getCollateralDecimals = (collateral: Coins): number => {
  const decimalsMap: Record<Coins, number> = {
    USDC: 6,
    USDCe: 6,
    USDT: 6,
    DAI: 18,
    WETH: 18,
    ETH: 18,
    OP: 18,
    ARB: 18,
    OVER: 18,
    THALES: 18,
    sTHALES: 18,
    USDbC: 6,
    cbBTC: 8,
    wBTC: 8,
  }

  return decimalsMap[collateral] || 18
}

export default useChainProvider
